import { Mutex } from 'async-mutex';
// @ts-ignore
import type * as RCL from 'rclnodejs';

import { info, warn, error, namespace } from '@sb/log';
import { ActionCancelledError } from '@sb/ros/errors/ActionCancelledError';
import { ActionTimeoutError } from '@sb/ros/errors/ActionTimeoutError';
import { GoalNotSucceededError } from '@sb/ros/errors/GoalNotSucceedError';
import { NoResponseError } from '@sb/ros/errors/NoResponseError';
import { getNode } from '@sb/ros/getNode';
import { getRCL } from '@sb/ros/getRCL';

/**
 * Base action class for sending goals to ROS2 action servers.
 *
 * To use, extend this class, then override the following methods:
 * - `getType()`: Return the type of the action as a string.
 * - `getTopic()`: Return the topic of the action as a string.
 * - `createGoal()`: Return the goal message to send to the action server.
 */
export abstract class BaseAction<
  ActionType extends RCL.TypeClass<RCL.ActionTypeClassName>,
  GoalType extends RCL.ActionGoal<ActionType>,
  ReturnType extends RCL.ActionResult<ActionType>,
> {
  // Time to wait for the service to be available on initial client creation
  // We want to wait a bit longer on initial client creation to account
  // for startup times
  public static CLIENT_CREATION_TIMEOUT_MS = 3000;

  // Time to wait for the existing client's server to become available
  public static WAIT_FOR_SERVER_TIMEOUT_MS = 100;

  // ROS2 GoalStatus constants (from action_msgs/msg/GoalStatus)
  private static readonly STATUS_SUCCEEDED = 4;

  private static readonly STATUS_CANCELED = 5;

  private static readonly STATUS_ABORTED = 6;

  // cache of the action client for each class
  private static clientMap: Map<string, any> = new Map<string, any>();

  // remove the action client from the cache, used mainly for testing
  public static deleteClient(topicName: string) {
    BaseAction.clientMap.delete(topicName);
  }

  /**
   * Return the ActionClient for a given action type + topic, creating it
   * via rclnodejs if not already cached. Exposed for non-instance consumers
   * (e.g., readiness probes at startup) so they share the same underlying
   * ActionClient as later BaseAction subclass calls — avoids duplicate DDS
   * discovery traffic on the same topic.
   */
  public static async getOrCreateClient<
    ActionType extends RCL.TypeClass<RCL.ActionTypeClassName>,
  >(type: ActionType, topic: string): Promise<RCL.ActionClient<ActionType>> {
    let client = BaseAction.clientMap.get(topic);

    if (!client) {
      const rcl = await getRCL();

      client = new rcl.ActionClient(await getNode(), type, topic);
      BaseAction.clientMap.set(topic, client);
    }

    return client;
  }

  /**
   * Poll until an action server advertises on the given type + topic, then
   * return. Loops indefinitely — caller is responsible for wrapping in a
   * timeout if needed. `onStillWaiting` is invoked each time `waitForServer`
   * returns false so callers can emit escalating log messages.
   *
   * Used by botman's heartbeat readiness gate (SUP-3795) to detect the end
   * of R2CB's main init phase by polling its TaskFromRobotGoal action server.
   */
  public static async waitForServerForever<
    ActionType extends RCL.TypeClass<RCL.ActionTypeClassName>,
  >(
    type: ActionType,
    topic: string,
    pollMs = 5000,
    onStillWaiting?: () => void,
  ): Promise<void> {
    const client = await BaseAction.getOrCreateClient(type, topic);

    while (!(await client.waitForServer(pollMs))) {
      onStillWaiting?.();
    }
  }

  protected goalHandle: RCL.ClientGoalHandle<ActionType> | undefined | void;

  private goalHandlePromise?: Promise<
    RCL.ClientGoalHandle<ActionType> | undefined | void
  >;

  private cancelled = false;

  private cancelPromise?: Promise<void>;

  private lastFeedbackDate: Date | undefined = undefined;

  private startListeningDate: Date = new Date();

  private static mutex = new Mutex();

  private get ns() {
    return namespace(`BaseAction:${this.getTopic()}`);
  }

  public async call(): Promise<ReturnType> {
    return this.getMutex().runExclusive(() => this.doCall());
  }

  private throwIfCancelled() {
    if (this.cancelled) {
      throw new ActionCancelledError('Goal cancelled');
    }

    // Also check the ROS2 goal handle status. This handles a race condition
    // where the action server cancels the goal (e.g., due to preemption) before
    // the client-side cancel() sets this.cancelled. If the goal handle reports
    // CANCELED, treat it as a clean cancellation rather than a failure.
    if (this.goalHandle && 'status' in this.goalHandle) {
      try {
        if (this.goalHandle.status === BaseAction.STATUS_CANCELED) {
          throw new ActionCancelledError('Goal cancelled by server');
        }
      } catch (e) {
        if (e instanceof ActionCancelledError) throw e;
        // Ignore status access errors
      }
    }
  }

  public async doCall(): Promise<ReturnType> {
    info(this.ns`sendGoal`, `creating goal`);
    const goalMessage = await this.createGoal();

    info(this.ns`sendGoal`, `sending goal request (${this.getTopic()})`);
    const client = await this.getClient();

    this.throwIfCancelled();

    this.goalHandlePromise = this.waitWithFeedbackTimeout(
      client.sendGoal(goalMessage, (feedback: any) => {
        this.onFeedback(feedback);
      }),
    );

    this.goalHandle = await this.goalHandlePromise;
    this.goalHandlePromise = undefined;

    if (!this.goalHandle) {
      throw new Error('Goal timed out');
    }

    this.throwIfCancelled();

    if (!this.goalHandle.isAccepted()) {
      info(this.ns`sendGoal`, 'goal rejected');

      throw new Error('Goal rejected');
    }

    info(this.ns`sendGoal`, 'goal accepted');

    const result = await this.waitWithFeedbackTimeout(
      this.goalHandle.getResult(),
    );

    if (!this.goalHandle.isSucceeded()) {
      this.throwIfCancelled();

      error(this.ns`sendGoal`, 'goal not succeeded');
      this.onGoalNotSucceeded(result);
    }

    if (!result) {
      throw new Error('Result has timed out');
    }

    info(this.ns`sendGoal`, 'goal succeeded');

    // Defensive cleanup on the success path: drop refs to the
    // ClientGoalHandle so it can't be pinned by any rclnodejs-internal
    // closure that survives the call. We deliberately do NOT clear on the
    // error path — callers may still invoke cancel() against a goal that
    // returned a non-success result without a terminal status (the primary
    // fix for the underlying _feedbackCallbacks leak lives in
    // patches/rclnodejs+0.23.3.patch).
    this.goalHandle = undefined;
    this.goalHandlePromise = undefined;

    return result;
  }

  public async cancel() {
    this.cancelled = true;

    if (this.cancelPromise) {
      await this.cancelPromise;

      return;
    }

    this.cancelPromise = this.performCancel();

    try {
      await this.cancelPromise;
    } finally {
      this.cancelPromise = undefined;
    }
  }

  /**
   * Check if a goal handle is in a terminal state (SUCCEEDED, ABORTED, or CANCELED).
   * This is used to avoid calling cancelGoal() or getResult() on already-terminated goals,
   * which can cause hangs.
   */
  private checkIsTerminalState(goalHandle: any): {
    isTerminal: boolean;
    goalStatus?: number;
    reason?: string;
  } {
    let goalStatus: number | undefined;

    if (goalHandle && 'status' in goalHandle) {
      try {
        goalStatus = goalHandle.status;
      } catch (err) {
        // If status getter fails, we can't determine terminal state
      }
    }

    const isTerminal =
      goalStatus !== undefined &&
      (goalStatus === BaseAction.STATUS_SUCCEEDED ||
        goalStatus === BaseAction.STATUS_ABORTED ||
        goalStatus === BaseAction.STATUS_CANCELED);

    let reason: string | undefined;

    if (isTerminal) {
      if (goalStatus === BaseAction.STATUS_SUCCEEDED) {
        reason = 'SUCCEEDED';
      } else if (goalStatus === BaseAction.STATUS_ABORTED) {
        reason = 'ABORTED';
      } else {
        reason = 'CANCELED';
      }
    }

    return { isTerminal, goalStatus, reason };
  }

  private async performCancel() {
    const goalHandle = await this.resolveGoalHandleForCancel();

    if (!goalHandle) {
      info(this.ns`cancel`, 'no goal to cancel');

      return;
    }

    const terminalCheckBeforeCancel = this.checkIsTerminalState(goalHandle);

    if (terminalCheckBeforeCancel.isTerminal) {
      info(
        this.ns`cancel`,
        'Goal already in terminal state, skipping cancelGoal()',
        {
          reason: terminalCheckBeforeCancel.reason,
        },
      );
    } else {
      try {
        await goalHandle.cancelGoal();
      } catch (err) {
        warn(this.ns`cancel`, 'cancelGoal() failed', {
          error: err instanceof Error ? err.message : err,
        });
      }
    }

    // Re-check status before getResult() - getResult() can hang for already-SUCCEEDED goals
    // There's a race condition where status shows SUCCEEDED but result isn't available yet
    const terminalCheckBeforeGetResult = this.checkIsTerminalState(goalHandle);

    const isAlreadySucceeded =
      terminalCheckBeforeGetResult.goalStatus === BaseAction.STATUS_SUCCEEDED;

    if (isAlreadySucceeded) {
      info(this.ns`cancel`, 'Goal already SUCCEEDED, skipping getResult()');

      return;
    }

    try {
      await goalHandle.getResult();

      info(this.ns`cancel`, `goal fully terminated`);
    } catch (err) {
      info(this.ns`cancel`, `goal terminated (with error/abort)`, err);
    }
  }

  private async resolveGoalHandleForCancel() {
    if (this.goalHandle) {
      return this.goalHandle;
    }

    if (!this.goalHandlePromise) {
      return undefined;
    }

    try {
      this.goalHandle = await this.goalHandlePromise;

      return this.goalHandle;
    } catch (err) {
      warn(
        this.ns`cancel`,
        'Failed to resolve goal handle before cancellation',
        err,
      );

      return undefined;
    }
  }

  private waitWithFeedbackTimeout(promise: Promise<any>): Promise<any> {
    this.startListeningDate = new Date();

    let interval: NodeJS.Timeout;

    const getTimeDiffSinceLastFeedback = () => {
      if (!this.lastFeedbackDate) {
        return new Date().getTime() - this.startListeningDate.getTime();
      }

      return new Date().getTime() - this.lastFeedbackDate.getTime();
    };

    const feedbackCheckPromise = new Promise<void>((_, reject) => {
      interval = setInterval(() => {
        const diff = getTimeDiffSinceLastFeedback();

        if (diff > this.getTimeout()) {
          clearInterval(interval);

          if (this.lastFeedbackDate !== undefined) {
            reject(
              new ActionTimeoutError(
                'Feedback was not received in the last second.',
              ),
            );
          }

          reject(new NoResponseError('Feedback was not received.'));
        }
      }, 100); // Check every 100ms
    });

    return Promise.race([promise, feedbackCheckPromise]).finally(() => {
      info(this.ns`createPromiseWithInterval`, 'clearing interval');

      if (interval) {
        clearInterval(interval);
      }
    });
  }

  protected createGoal(): Promise<GoalType> {
    throw new Error('Method not implemented.');
  }

  protected async getClient(): Promise<RCL.ActionClient<ActionType>> {
    let client = BaseAction.clientMap.get(this.getTopic());

    if (!client) {
      const rcl = await getRCL();

      client = new rcl.ActionClient(
        await getNode(),
        this.getType(),
        this.getTopic(),
      );

      BaseAction.clientMap.set(this.getTopic(), client);

      info(this.ns`getClient`, `waiting for action server...`);

      await client.waitForServer(BaseAction.CLIENT_CREATION_TIMEOUT_MS);
    }

    return client;
  }

  /**
   * Get the ros2 action type as a string
   * example: 'standard_bots_msgs/action/Execute'
   */
  protected abstract getType(): RCL.TypeClass<keyof RCL.ActionsMap>;

  /**
   * Get the ros2 action topic
   * example: '/execute'
   */
  protected abstract getTopic(): string;

  /**
   * Timeout in ms for the feedback check
   */
  protected getTimeout(): number {
    return 3000;
  }

  protected onFeedback(_: any) {
    this.lastFeedbackDate = new Date();
  }

  protected getMutex(): Mutex {
    return BaseAction.mutex;
  }

  /**
   * Called when a goal does not succeed. Throws an appropriate error.
   * Subclasses can override to throw a more specific error type
   * (e.g., PlanningGoalNotSucceededError, ExecutionGoalNotSucceededError).
   * @param _result - The result from the action (may contain error details)
   */
  protected onGoalNotSucceeded(_result: ReturnType | undefined): never {
    throw new GoalNotSucceededError('Goal not succeeded');
  }
}
